import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Pill, Plus } from 'lucide-react';
import {
  DAILY_LIFE_COPY,
  EMPTY_STATES,
  MEDICATION_STATUS_LABELS,
  MEDICATION_STORAGE_OPTIONS,
  MEDICINE_TYPES,
  ROLES,
  isTimeOfDay,
  type MedicationStatus,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  FieldGroup,
  Input,
  Spinner,
  Textarea,
} from '@/components/ui';
import { api, apiData } from '@/lib/api';
import { uploadMediaAsset } from '@/lib/upload';
import { formatDate, formatTime, todayISO } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { ChildSummary, MedicationRequest } from '@/types';

/** 투약의뢰서 — 명세서 §5.9 */
export function MedicationsPage() {
  const membership = useAuth((s) => s.membership);
  const isParent = membership?.role === ROLES.PARENT;
  const [composing, setComposing] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['medications'],
    queryFn: () => apiData<MedicationRequest[]>('/medications'),
  });

  const requests = data ?? [];

  return (
    <Screen>
      <AppBar
        title="투약의뢰서"
        back
        right={
          isParent && (
            <Button size="sm" variant="ghost" icon={<Plus size={16} />} onClick={() => setComposing(true)}>
              쓰기
            </Button>
          )
        }
      />

      {composing && <MedicationForm onClose={() => setComposing(false)} />}

      {isPending && <Spinner />}
      {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

      {!isPending && requests.length === 0 && !composing && (
        <EmptyState
          title={EMPTY_STATES.medication.title}
          body={EMPTY_STATES.medication.body}
          action={
            isParent && (
              <Button onClick={() => setComposing(true)}>{EMPTY_STATES.medication.action}</Button>
            )
          }
        />
      )}

      <ul className="flex flex-col gap-3 p-4">
        {requests.map((request) => (
          <MedicationCard key={request.id} request={request} canAdminister={!isParent} />
        ))}
      </ul>
    </Screen>
  );
}

function MedicationCard({
  request,
  canAdminister,
}: {
  request: MedicationRequest;
  canAdminister: boolean;
}) {
  const queryClient = useQueryClient();

  const administer = useMutation({
    mutationFn: () => api(`/medications/${request.id}/administer`, { method: 'POST', body: {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['medications'] });
    },
  });

  return (
    <Card as="li">
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        <Chip tone={toneOf(request.status)}>{MEDICATION_STATUS_LABELS[request.status]}</Chip>
        {request.child && <Chip tone="neutral">{request.child.name}</Chip>}
        <span className="ml-auto text-sm text-ink-sub">{formatDate(request.date)}</span>
      </div>

      <p className="font-semibold">{request.symptom}</p>

      {request.photoUrl && (
        <img src={request.photoUrl} alt="약 사진" className="mt-2 h-24 w-24 rounded-md object-cover" />
      )}
      <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-base">
        <dt className="text-ink-sub">약</dt>
        <dd>
          {request.medicineType} · {request.dosage}
        </dd>
        <dt className="text-ink-sub">시간</dt>
        <dd className="tnum">{request.times.join(', ')}</dd>
        {request.storage && (
          <>
            <dt className="text-ink-sub">보관</dt>
            <dd>{request.storage}</dd>
          </>
        )}
      </dl>

      {request.note && <p className="mt-2 text-base text-ink-sub">{request.note}</p>}

      {request.status === 'ADMINISTERED' && request.administeredAt && (
        <p className="mt-2 flex items-center gap-1.5 rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">
          <Check size={14} /> {formatTime(request.administeredAt)}에 투약을 마쳤어요
        </p>
      )}

      {canAdminister && request.status === 'REQUESTED' && (
        <Button
          className="mt-3"
          block
          icon={<Pill size={16} />}
          loading={administer.isPending}
          onClick={() => administer.mutate()}
        >
          투약 완료 처리
        </Button>
      )}
      {administer.isError && (
        <p className="mt-2 text-sm text-notice">{(administer.error as Error).message}</p>
      )}
    </Card>
  );
}

function toneOf(status: MedicationStatus): 'primary' | 'secondary' | 'neutral' {
  if (status === 'ADMINISTERED') return 'primary';
  if (status === 'REQUESTED') return 'secondary';
  return 'neutral';
}

function MedicationForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: children } = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });

  const [childId, setChildId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [symptom, setSymptom] = useState('');
  const [medicineType, setMedicineType] = useState<string>(MEDICINE_TYPES[0]);
  const [dosage, setDosage] = useState('');
  const [times, setTimes] = useState<string[]>(['12:30']);
  const [storage, setStorage] = useState<string>(MEDICATION_STORAGE_OPTIONS[0]);
  const [note, setNote] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [photo, setPhoto] = useState<{ mediaId: string; preview: string } | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const activeChildId = childId || children?.[0]?.id || '';
  const timesValid = times.length > 0 && times.every(isTimeOfDay);

  const submit = useMutation({
    mutationFn: () =>
      api('/medications', {
        method: 'POST',
        body: {
          childId: activeChildId,
          date,
          symptom,
          medicineType,
          dosage,
          times,
          storage,
          note: note || undefined,
          signatureKey: signature ?? undefined,
          photoMediaId: photo?.mediaId,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['medications'] });
      onClose();
    },
  });

  const canSubmit = Boolean(activeChildId && symptom && dosage && timesValid && signature);

  return (
    <Card className="m-4">
      <p className="mb-3 font-bold">투약의뢰서 작성</p>

      {(children?.length ?? 0) > 1 && (
        <FieldGroup label="아이">
          <div className="flex flex-wrap gap-2">
            {children?.map((child) => (
              <Chip
                key={child.id}
                tone={child.id === activeChildId ? 'primary' : 'neutral'}
                selected={child.id === activeChildId}
                onClick={() => setChildId(child.id)}
              >
                {child.name}
              </Chip>
            ))}
          </div>
        </FieldGroup>
      )}

      <Field label="날짜">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>

      <Field label="증상">
        <Input
          value={symptom}
          onChange={(e) => setSymptom(e.target.value)}
          placeholder="예: 콧물, 기침"
        />
      </Field>

      <FieldGroup label="약 종류">
        <div className="flex flex-wrap gap-2">
          {MEDICINE_TYPES.map((type) => (
            <Chip
              key={type}
              tone={type === medicineType ? 'primary' : 'neutral'}
              selected={type === medicineType}
              onClick={() => setMedicineType(type)}
            >
              {type}
            </Chip>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="약 사진" hint="어떤 약인지 사진으로 남겨 두면 선생님이 헷갈리지 않아요.">
        {photo ? (
          <div className="flex items-center gap-3">
            <img src={photo.preview} alt="약 사진" className="h-20 w-20 rounded-md object-cover" />
            <Button size="sm" variant="outline" onClick={() => setPhoto(null)}>
              사진 지우기
            </Button>
          </div>
        ) : (
          <label className="tap inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-line-strong bg-surface px-3 py-2 text-sm font-medium">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                setPhotoUploading(true);
                uploadMediaAsset(file)
                  .then(({ mediaId }) => setPhoto({ mediaId, preview: URL.createObjectURL(file) }))
                  .finally(() => setPhotoUploading(false));
              }}
            />
            {photoUploading ? '올리는 중…' : '사진 선택 (선택)'}
          </label>
        )}
      </FieldGroup>

      <Field label="용량">
        <Input
          value={dosage}
          onChange={(e) => setDosage(e.target.value)}
          placeholder="예: 1회 5ml"
        />
      </Field>

      <Field label="투약 시간">
        <div className="flex flex-col gap-2">
          {times.map((time, index) => (
            <div key={index} className="flex gap-2">
              <Input
                type="time"
                value={time}
                onChange={(e) =>
                  setTimes((prev) => prev.map((t, i) => (i === index ? e.target.value : t)))
                }
              />
              {times.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTimes((prev) => prev.filter((_, i) => i !== index))}
                >
                  삭제
                </Button>
              )}
            </div>
          ))}
          {times.length < 8 && (
            <Button variant="outline" size="sm" onClick={() => setTimes((prev) => [...prev, '15:00'])}>
              시간 추가
            </Button>
          )}
        </div>
      </Field>

      <FieldGroup label="보관">
        <div className="flex flex-wrap gap-2">
          {MEDICATION_STORAGE_OPTIONS.map((option) => (
            <Chip
              key={option}
              tone={option === storage ? 'primary' : 'neutral'}
              selected={option === storage}
              onClick={() => setStorage(option)}
            >
              {option}
            </Chip>
          ))}
        </div>
      </FieldGroup>

      <Field label="전달사항">
        <Textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="선생님께 전하고 싶은 말씀"
        />
      </Field>

      <FieldGroup label="서명" hint={DAILY_LIFE_COPY.medication.signatureHint}>
        <SignaturePad onChange={setSignature} />
      </FieldGroup>

      <p className="mb-3 text-sm text-ink-sub">{DAILY_LIFE_COPY.medication.consentNotice}</p>

      {submit.isError && (
        <p className="mb-2 text-sm text-notice">{(submit.error as Error).message}</p>
      )}

      <div className="flex gap-2">
        <Button variant="outline" block onClick={onClose}>
          취소
        </Button>
        <Button block disabled={!canSubmit} loading={submit.isPending} onClick={() => submit.mutate()}>
          제출
        </Button>
      </div>
    </Card>
  );
}

/**
 * 터치 전자서명 (§5.9).
 * 캔버스에 그린 획을 data URL로 만들어 그대로 저장한다. 서명은 원본 이미지가 곧 증거이므로
 * 벡터화하거나 다시 그리지 않는다.
 */
function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const pointAt = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const context = canvasRef.current?.getContext('2d');
    const point = pointAt(event);
    if (!context || !point) return;
    drawing.current = true;
    context.lineWidth = 2.5;
    context.lineCap = 'round';
    context.strokeStyle = '#2F3A33';
    context.beginPath();
    context.moveTo(point.x, point.y);
    canvasRef.current?.setPointerCapture(event.pointerId);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const context = canvasRef.current?.getContext('2d');
    const point = pointAt(event);
    if (!context || !point) return;
    context.lineTo(point.x, point.y);
    context.stroke();
    setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    if (canvas && hasInk) onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={600}
        height={200}
        className="h-32 w-full touch-none rounded-sm border border-line-strong bg-surface"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <Button variant="ghost" size="sm" className="mt-1" onClick={clear}>
        다시 서명
      </Button>
    </div>
  );
}
